import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ExpertApplication } from "redux/models/expertProfile";
import { IExpertArea } from "../../types/components/expert";

export enum StatusType {
  "SAVE_DRAFT",
  "APPLY",
}

export enum FormType {
  "CREATE",
  "UPDATE",
}

const qualificationTypes: any = {
  0: "expert",
  1: "type2"
};

export interface ExpertProfile {
  profile: ExpertApplication;
  loading: boolean;
  error: any;
  isFormDisabled: boolean;
  formType: FormType;
}

const initialState: ExpertProfile = {
  profile: {
    id: null,
    status: null,
    first_name: { value: "", error: null },
    last_name: { value: "", error: null },
    country_id: { value: "", error: null },
    address: { value: "", error: null },
    telephone: { value: "", error: null },
    email: { value: "", error: null },
    career_summary: { value: "", error: null },
    zoom_link: { value: "", error: null },
    contact_medium: [{ medium: "", value: "", error: null }],
    languages: [{ language: "", level: "", error: null }],
    qualifications: [
      {
        type: "",
        title: "",
        certification: null,
        oldCertification: { certification: null, disable: false },
        error: null
      }
    ],
    certificate1: [{ type: "", file: null, error: null }],
    certificate2: [{ type: "", file: null, error: null }],
    country_name: { value: "", error: null },
    image: { value: null, error: null },
    country_code: { value: "", error: null }
  },
  loading: false,
  error: null,
  isFormDisabled: false,
  formType: FormType.CREATE
};

export const expertApplicationSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    changeExpertProfile: (state, action: PayloadAction<any>) => {
      state.profile = {
        ...state.profile,
        [action.payload.key]: {
          value: action.payload.value,
          error: action.payload.error
        }
      };
    },
    expertProfileActionRequest: (state) => {
      state.loading = true;
    },
    getExpertProfileSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.profile = {
        id: action.payload.id,
        status: action.payload.status,
        first_name: { value: action.payload.first_name, error: null },
        last_name: { value: action.payload.last_name, error: null },
        country_id: { value: action.payload.country_id, error: null },
        country_code: { value: action.payload.country_code, error: null },
        address: { value: action.payload.address, error: null },
        email: { value: action.payload.email, error: null },
        telephone: { value: action.payload.telephone, error: null },
        career_summary: { value: action.payload.career_summary, error: null },
        zoom_link: { value: action.payload.zoom_link, error: null },
        languages: Array.isArray(action.payload?.languages)
        && action.payload?.languages.length > 0
          ? action.payload?.languages?.map((language: any) => {
            return {
              language: language.language ?? "",
              level: language.level ?? "",
              error: null
            };
          }) : [{ language: "", level: "", error: null }],
        contact_medium: Array.isArray(action.payload?.contact_medium)
        && action.payload?.contact_medium.length > 0
          ? action.payload.contact_medium?.map(
            (contact_medium: any) => {
              return {
                ...contact_medium,
                value: contact_medium.contact ?? "",
                medium: contact_medium.type ?? "",
                error: null
              };
            }
          ) : [{ medium: "", value: "", error: null }],
        qualifications: action.payload?.qualifications.length > 0
          ? action.payload.qualifications?.map(
            (qualification: any) => {
              return {
                type: qualification.qualifiction_type ?? "",
                title: qualification.title ?? "",
                certification: null,
                oldCertification: {
                  certification: qualification.thumbnail,
                  disable: false
                },
                error: null
              }
                ;
            }
          ) : [
            { type: "", title: "", certification: null, error: null }
          ],
        certificate1: Array.isArray(action.payload.certification)
        && action.payload.certification.length > 0 ? [
          {
            type: action.payload.certification?.[0].type ?? "",
            file: null,
            oldCertificate: { certificate: action.payload.certification?.[0].thumbnail || null, disable: false },
            error: null
          }
        ] : [{ type: "", file: null, error: null, oldCertificate: { certificate: null, disable: false } }],
        certificate2: Array.isArray(action.payload.certification)
        && action.payload.certification.length > 1 ? [
          {
            type: action.payload.certification?.[1].type ?? "",
            file: null,
            oldCertificate: { certificate: action.payload.certification?.[1].thumbnail || null, disable: false },
            error: null
          }
        ] : [{ type: "", file: null, error: null, oldCertificate: { certificate: null, disable: false } }],
        country_name: { value: action.payload.country.name, error: null },
        image: { value: action.payload.thumbnail, error: null }
      };
      // state.isFormDisabled = action.payload.status !== StatusType.SAVE_DRAFT;
      state.formType = FormType.UPDATE;
    },
    expertProfileActionFailure: (state) => {
      state.loading = false;
    },
    expertProfileActionSuccess: (state) => {
      state.loading = false;
    },
    addRemoveMediumOfContactRequest: (state, action: PayloadAction<number>) => {
      if (action.payload === 0) {
        state.profile.contact_medium = [
          ...state.profile?.contact_medium,
          { medium: "", value: "", error: null }
        ];
      } else {
        state.profile.contact_medium = state.profile.contact_medium?.filter(
          (_, i) => i !== action.payload
        );
      }
    },
    addRemoveQualifications: (state, action: PayloadAction<number>) => {
      if (action.payload === 0) {
        state.profile.qualifications = [
          ...state.profile?.qualifications,
          {
            type: "",
            title: "",
            certification: null,
            error: null
          }
        ];
      } else {
        state.profile.qualifications = state.profile.qualifications?.filter(
          (_, i) => i !== action.payload
        );
      }
    },
    changeMediumOfContactData: (state: any, action: PayloadAction<any>) => {
      const applicationKey: keyof ExpertApplication = action.payload.field;
      state.profile[applicationKey] = state.profile[applicationKey]?.map(
        (item: any, index: number) => {
          if (action.payload.index === index) {
            return {
              ...item,
              error: action.payload.error,
              [action.payload.key]: action.payload.value
            };
          }
          return item;
        }
      );
    },
    changeQualifications: (state, action: PayloadAction<any>) => {
      state.profile.qualifications = state.profile.qualifications?.map(
        (item, index) => {
          if (action.payload.index === index) {
            return {
              ...item,
              error: null,
              [action.payload.key]: action.payload.value
            };
          }
          return item;
        }
      );
    },
    addRemoveLanguagesRequest: (state, action: PayloadAction<number>) => {
      if (action.payload === 0) {
        state.profile.languages = [
          ...state.profile?.languages,
          { language: "", level: "", error: "" }
        ];
      } else {
        state.profile.languages = state.profile.languages?.filter(
          (_, i) => i !== action.payload
        );
      }
    },
    changeLanguagessData: (state, action: PayloadAction<any>) => {
      state.profile.languages = state.profile.languages?.map((item, index) => {
        if (action.payload.index === index) {
          return {
            language: action.payload.languages,
            level: action.payload.level,
            error: null
          };
        }
        return item;
      });
    },
    addRemoveLanguages: (state, action: PayloadAction<number>) => {
      if (action.payload === 0) {
        state.profile.languages = [
          ...state.profile?.languages,
          { language: "", level: "", error: null }
        ];
      } else {
        state.profile.languages = state.profile.languages?.filter(
          (_, i) => i !== action.payload
        );
      }
    },
    resetExpertForm: (state) => {
      state.profile = initialState.profile;
    },
    validateExpertProfileForm: (
      state,
      action: PayloadAction<ExpertApplication>
    ) => {
      state.profile = { ...action.payload };
    },
    enableForm: (state, action: PayloadAction<any>) => {
      state.isFormDisabled = action.payload;
    },
    contactUsRequest: (state) => {
      state.loading = true;
    },
    contactUsSuccess: (state) => {
      state.loading = false;
    },
    contactUsFailure: (state) => {
      state.loading = false;
    }
  }
});

export const {
  changeExpertProfile,
  expertProfileActionRequest,
  expertProfileActionFailure,
  expertProfileActionSuccess,
  getExpertProfileSuccess,
  addRemoveMediumOfContactRequest,
  changeMediumOfContactData,
  addRemoveLanguagesRequest,
  changeLanguagessData,
  validateExpertProfileForm,
  enableForm,
  changeQualifications,
  addRemoveQualifications,
  addRemoveLanguages,
  contactUsRequest,
  contactUsSuccess,
  contactUsFailure,
  resetExpertForm
} = expertApplicationSlice.actions;

export default expertApplicationSlice.reducer;
